import { FC, useState } from 'react';
import {
  Button,
  Icon,
  RemoveAlertDialog,
  useDisclosure,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useflowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';
import { useAnvaQuotesCalculate } from '@flows/src/api/anva-flows/getAnvaQuotes';
import FlowProductsCalculateError from '@flows/src/pages/flow-products/FlowProductsCalculateError';

interface Props {
  sessionId: string;
}

const BasketCalculateButton: FC<Props> = ({ sessionId }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [errorObj, setErrorObj] = useState<Error>();
  const { t } = useTranslation();

  const packages = useflowsProductBasketStore((state) => state.packages);
  const insurances = useflowsProductBasketStore((state) => state.insurances);
  const setPackages = useflowsProductBasketStore((state) => state.setPackages);
  const setInsurances = useflowsProductBasketStore(
    (state) => state.setInsurances,
  );
  const setIsBasketValidated = useflowsProductBasketStore(
    (state) => state.setIsBasketValidated,
  );
  const { mutateAsync, isLoading } = useAnvaQuotesCalculate();

  const handleRecalculate = async () => {
    try {
      const responseData = await mutateAsync({
        sessionId,
        payload: {
          chosen_insurance_packages: packages.map((pkg) => ({
            ...pkg,
            name: pkg.name,
            code: pkg.code,
            chosen_insurance_policies: pkg.quotes.map((quote) => ({
              anva_product_id: quote.anva_product_id,
              index: quote.index,
              coverages: quote.coverages.map(({ code }) => Number(code)),
            })),
          })),
          chosen_insurance_policies: insurances.map((quote) => ({
            ...quote,
            anva_product_id: quote.anva_product_id,
            index: quote.index,
            coverages: quote.coverages.map(({ code }) => Number(code)),
            start_date: quote.start_date,
            payment_interval: quote.payment_interval,
          })),
        },
      });
      /** Update packages in store to align with backend */
      setPackages(responseData.packages);
      setInsurances(responseData.quotes);
      setIsBasketValidated(true);
    } catch (error) {
      setErrorObj(error as Error);
      onOpen();
    }
  };

  return (
    <>
      <RemoveAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onClose}
        title={t('Calculation failed')}
        hideCancelButton
        confirmButtonText={t('CLOSE')}
        size="2xl"
      >
        <FlowProductsCalculateError error={errorObj as Error} />
      </RemoveAlertDialog>
      <Button
        variant="secondary"
        onClick={handleRecalculate}
        leftIcon={<Icon name="GeneralActionsRefreshOrReload" />}
        isLoading={isLoading}
      >
        {t('Re-calculate')}
      </Button>
    </>
  );
};

export default BasketCalculateButton;
